import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import AppLayout from "@amzn/awsui-components-react-v3/polaris/app-layout";
import Header from '@amzn/awsui-components-react-v3/polaris/header';
import SpaceBetween from '@amzn/awsui-components-react-v3/polaris/space-between';
import Alert from '@amzn/awsui-components-react-v3/polaris/alert';
import Grid from '@amzn/awsui-components-react-v3/polaris/grid';
import Button from '@amzn/awsui-components-react-v3/polaris/button';
import Spinner from '@amzn/awsui-components-react-v3/polaris/spinner';
import LoadingContent from '../../components/LoadingContent/LoadingContent';
import { SessionKey, MINIMUM_SBTS_SEATS_PER_REQUEST_CUSTOMER, MAX_NUMBER_OF_MIDTERM_SEATS, AwsAccountType } from '../../common/constants';
import { ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT } from "../../common/routes";
import SBTSBreadcrumbGroup from '../../components/BreadcrumbGroup/BreadcrumbGroup';
import SubscriptionRenewalFormContainer from '../../components/SubscriptionRenewalFormContainer/SubscriptionRenewalFormContainer';
import { useUpdateAutoRenewalMutation } from '../../user/api/mutations';
import { getSubscriptionRequestBySubscriptionId, useGetOfferPriceForAccount } from '../../user/api/queries';
import ModifySubscriptionDetailsContainer from '../../components/SubscriptionDetailsFormContainer/ModifySubscriptionDetailsContainer';
import ModifySubscriptionBillingContainer from '../../components/BillingContainer/ModifySubscriptionBillingContainer';
import { SubscriptionSummaryContainer } from "../../components/SubscriptionSummaryContainer/SubscriptionSummaryContainer";
import { UserContext } from '../../App';
import { generateAwsSignInUrl, verifyJwt } from "../../auth/awsSignIn";
const verifyJwtToken = (user, awsAccountJwt, setIsJwtTokenValid) => {
    verifyJwt(awsAccountJwt)
        .then(() => {
        try {
            setIsJwtTokenValid(true);
        }
        catch (err) {
            console.error(err);
            throw err;
        }
    })
        .catch(() => {
        setIsJwtTokenValid(false);
        window.location.assign(generateAwsSignInUrl({ userId: user.id }));
    });
};
export const SubscriptionRenewalPageLayout = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isJwtValid, setIsJwtTokenValid] = useState(false);
    const [awsJwtToken, setAwsJwtToken] = useState();
    const [awsAccountId, setAwsAccountId] = useState();
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [orderDetails, setOrderDetails] = useState(null);
    const [formData, setFormData] = useState({
        renewalSeatsCount: { value: '', error: undefined },
        autoRenewal: true,
        optOutReason: { value: '', error: undefined },
        agreementChecked: true
    });
    const [discountPercentage, setDiscountPercentage] = useState();
    const user = useContext(UserContext);
    const { updateAutoRenewal, updateAutoRenewalLoading } = useUpdateAutoRenewalMutation();
    const id = sessionStorage.getItem(SessionKey.SUBSCRIPTION_REQUEST_ID);
    const { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError } = getSubscriptionRequestBySubscriptionId(id);
    const { data: offerPriceData, loading: offerPriceDataLoading, error: offerPriceDataError } = useGetOfferPriceForAccount(sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID), user?.id, user?.email, (sessionStorage.getItem(SessionKey.IS_AMAZON_TRAINING_PARTNER) ? true : false), user?.vibeId);
    useEffect(() => {
        if (subscriptionData?.subscriptionRequestById) {
            const subscription = subscriptionData.subscriptionRequestById;
            const awsAccountId = sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID);
            const awsAccountJwt = sessionStorage.getItem(SessionKey.AWS_ACCOUNT_JWT);
            if (awsAccountId && awsAccountJwt) {
                setAwsAccountId(awsAccountId);
                setAwsJwtToken(awsAccountJwt);
                verifyJwtToken(user, awsAccountJwt, setIsJwtTokenValid);
            }
            else {
                window.location.assign(generateAwsSignInUrl({ userId: user.id }));
            }
            setOrderDetails({
                orderId: subscription.orderId || 'NA',
                originalSeats: subscription.numberOfSeats,
                subscriptionEndDate: subscription.subscriptionEndDate,
                pricePerSeat: subscription.pricePerSeat,
                accountId: subscription.linkedAccountId,
                discountPercentage: subscription.discountPercentage || 0,
                partnerRequest: subscription.partnerRequest,
                autoRenewal: subscription.autoRenewal !== undefined ? subscription.autoRenewal : 'NA',
            });
            setDiscountPercentage(subscription.discountPercentage);
            setFormData(prev => ({
                ...prev,
                renewalSeatsCount: {
                    value: subscription.numberOfSeats?.toString() || '',
                    error: undefined
                },
                autoRenewal: typeof subscription.autoRenewal === 'boolean' ? subscription.autoRenewal : false,
            }));
        }
        else if (!subscriptionLoading && !subscriptionError && id) {
            navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, {
                state: { notification: { text: t('subscription_form_something_went_wrong'), type: 'error' } }
            });
        }
    }, [subscriptionData]);
    const validateRenewalSeatsCount = (value) => {
        if (!value) {
            return t("subscription_form_number_of_seats_required_error_text");
        }
        if (value && Number.isNaN(parseInt(value))) {
            return t('subscription_form_number_of_seats_should_be_number_error_text');
        }
        if (value && !Number.isInteger(parseFloat(value))) {
            return t('subscription_form_number_of_seats_should_be_integer_error_text');
        }
        if (value && +value < MINIMUM_SBTS_SEATS_PER_REQUEST_CUSTOMER) {
            return t('subscription_form_minimum_number_of_seats_error_text', { minSeats: MINIMUM_SBTS_SEATS_PER_REQUEST_CUSTOMER });
        }
        if (value && +value > MAX_NUMBER_OF_MIDTERM_SEATS) {
            return t('subscription_form_number_of_seats_should_be_at_less_than_10000_error_text');
        }
        return '';
    };
    const constructEventObject = (value) => {
        return {
            detail: {
                value
            }
        };
    };
    const handleFormDataChange = {
        renewalSeatsCount: (e) => {
            const value = e.detail.value;
            const error = validateRenewalSeatsCount(value);
            setFormData(prev => ({
                ...prev,
                renewalSeatsCount: {
                    value,
                    error
                }
            }));
            return !error;
        },
        optOutReason: (e) => {
            const value = e.detail.selectedOption?.value || '';
            setFormData(prev => ({
                ...prev,
                optOutReason: {
                    value,
                    error: undefined
                }
            }));
            return true;
        },
        autoRenewal: (checked) => {
            setFormData(prev => ({
                ...prev,
                autoRenewal: checked
            }));
            return true;
        },
        agreementChecked: (e) => {
            setFormData(prev => ({
                ...prev,
                agreementChecked: e.detail.checked
            }));
            return true;
        },
    };
    const validateForm = () => {
        let isFormValid = true;
        if (formData.autoRenewal) {
            const renewalSeatsCountValid = handleFormDataChange.renewalSeatsCount(constructEventObject(formData.renewalSeatsCount.value));
            isFormValid = renewalSeatsCountValid;
        }
        return isFormValid;
    };
    const handleSubscriptionRenewalSubmit = () => {
        if (!validateForm()) {
            console.log('SubscriptionRenewalForm not valid.');
            return;
        }
        const updateInput = {
            id: subscriptionData.subscriptionRequestById.id,
            autoRenewal: formData.autoRenewal,
            awsToken: awsJwtToken,
            optOutReason: !formData.autoRenewal && formData.optOutReason.value ? formData.optOutReason.value : undefined,
            renewalSeatsCount: formData.autoRenewal ? parseInt(formData.renewalSeatsCount.value) : undefined
        };
        updateAutoRenewal({
            variables: { input: updateInput },
            onCompleted: () => {
                sessionStorage.removeItem(SessionKey.SUBSCRIPTION_REQUEST_ID);
                sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_ID);
                sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_JWT);
                navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, {
                    state: {
                        notification: {
                            text: "Your subscription renewal preferences have been updated successfully.",
                            type: 'success'
                        }
                    }
                });
            },
            onError: (error) => {
                setShowError(true);
                setErrorMessage('Failed to update subscription. Please try again.');
            }
        });
    };
    if (!awsAccountId || !isJwtValid || subscriptionLoading || !orderDetails) {
        return _jsx(LoadingContent, { text: "Loading" });
    }
    return (_jsx(AppLayout, { content: _jsxs(SpaceBetween, { size: "l", children: [_jsx(SBTSBreadcrumbGroup, { items: [
                        { text: "Subscription management", href: "/" },
                        { text: "Modify subscription", href: "#" }
                    ] }), _jsx(Header, { variant: "h1", actions: _jsxs(SpaceBetween, { direction: "horizontal", size: "xs", children: [_jsx(Button, { onClick: e => {
                                    navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT);
                                }, children: t("cancel_text") }), _jsx(Button, { onClick: handleSubscriptionRenewalSubmit, variant: "primary", disabled: updateAutoRenewalLoading, children: updateAutoRenewalLoading ? (_jsxs(SpaceBetween, { direction: "horizontal", size: "xs", children: [_jsx(Spinner, {}), _jsx("span", { children: "Submitting..." })] })) : 'Submit request' })] }), children: t("subscription_renewal_page_header") }), showError && (_jsx(Alert, { type: "error", header: "Error", dismissible: true, onDismiss: () => setShowError(false), children: errorMessage })), _jsxs(Grid, { gridDefinition: [
                        { colspan: { default: 12, xxs: 7 } },
                        { colspan: { default: 12, xxs: 5 } },
                    ], children: [_jsxs(SpaceBetween, { size: "l", children: [_jsx(ModifySubscriptionDetailsContainer, { orderDetails: orderDetails, isRenewalPage: true }), _jsx(SubscriptionRenewalFormContainer, { formData: formData, onFormDataChange: handleFormDataChange }), _jsx(ModifySubscriptionBillingContainer, { accountId: orderDetails.accountId })] }), _jsx(SubscriptionSummaryContainer, { numberOfSeats: formData.renewalSeatsCount.value, awsAccountId: subscriptionData.awsAccountId, offerPriceData: offerPriceData, awsAccountType: AwsAccountType.REGULAR, setDiscount: setDiscountPercentage, formData: formData, onFormDataChange: handleFormDataChange })] })] }), navigationHide: true, toolsHide: true }));
};
export default SubscriptionRenewalPageLayout;
